import type { ChartId } from 'charts/catalog/chartCatalog';
import type { AnalyticsChart } from 'charts/catalog/types';
import { useIsOffline } from 'components/common/withIsOffline';
import { moveDefaultLengthRangeToToday } from 'charts/common/filterValues';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isDateRangeObject } from 'shared/types/analytics';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import { clearedSectionFilterValue, isSectionFilterActive } from './model';
import { useProjectId, useProjectTimezone } from 'store/project';
import { useChartAvailableFieldsSet } from '../filtersRenderer/useChartAvailableFieldsSet';
import {
  applySectionFilterValue,
  hasCustomSharedFilters,
  resolveSharedFilterState,
  restoreSectionValues,
  type SectionChartRuntime,
  type SectionFiltersDefinition,
  SectionSharedFilter,
  type SharedFilterState,
  type SharedFilterValues,
} from './sharedFilters';

export type SectionChart = Readonly<{
  chartId: ChartId;
  runtime: SectionChartRuntime;
}>;

export type SectionSharedFiltersModel = Readonly<{
  /** The filters of the dimensions visible in the project (hidden issue fields have none). */
  states: readonly SharedFilterState[];
  /** Applied at once to the charts that follow the section; charts with their own value keep it. */
  setValue: (shared: SectionSharedFilter, value: unknown) => void;
  /** Clears one filter: nothing chosen, or the default date range. */
  clear: (shared: SectionSharedFilter) => void;
  /** Clears every active filter of the section. */
  clearAll: () => void;
  /** The filters that narrow the charts (something chosen, or another date range). */
  activeFilters: ReadonlySet<SectionSharedFilter>;
  /** Some charts have their own filters, which override the section's. */
  isMixed: boolean;
  customChartIds: ReadonlySet<ChartId>;
  /** The chart follows the section again: its own values of the shared filters give way to the section's. */
  restoreSectionFilters: (chartId: ChartId) => void;
  disabled: boolean;
}>;

type ChosenValues = Readonly<Partial<Record<SectionSharedFilter, unknown>>>;

/** Charts rendered in a section, paired with their runtimes (charts without one are skipped). */
export function toSectionCharts(
  charts: readonly AnalyticsChart[],
  runtimeById: Readonly<Partial<Record<ChartId, SectionChartRuntime>>>,
  isChartId: (id: string) => id is ChartId
): SectionChart[] {
  return charts.flatMap((chart) => {
    if (!isChartId(chart.id)) {
      return [];
    }
    const runtime = runtimeById[chart.id];
    return runtime ? [{ chartId: chart.id, runtime }] : [];
  });
}

function isVisible({ filter }: SharedFilterState, availableFields: ReadonlySet<string>): boolean {
  return !filter.fieldRelation || availableFields.has(filter.fieldRelation);
}

function toValues(states: readonly SharedFilterState[]): SharedFilterValues {
  return new Map(states.map((state) => [state.shared, state.value]));
}

/**
 * The values chosen before, for the project and section. A date range as long
 * as the default one moves to end today, as the charts' own ranges do:
 * otherwise, the next day, every chart would differ from the section.
 */
function readChosenValues(
  projectId: string | undefined,
  definition: SectionFiltersDefinition,
  timezone: string
): ChosenValues {
  if (!projectId) {
    return {};
  }
  const stored: ChosenValues = LocalStorageManager.getAnalyticsSectionFilters(projectId, definition.section);
  const dateRange = stored[SectionSharedFilter.dateRange];
  return isDateRangeObject(dateRange)
    ? { ...stored, [SectionSharedFilter.dateRange]: moveDefaultLengthRangeToToday(dateRange, timezone) }
    : stored;
}

/**
 * The filters of a dashboard section (PT-6804): a value chosen here is applied
 * at once, without an apply button, to every chart that follows the section.
 * A chart whose filter was set in its details keeps it: its own filters
 * override the section's. Chosen values are kept per project and section.
 */
export function useSectionSharedFilters(
  charts: readonly SectionChart[],
  definition: SectionFiltersDefinition
): SectionSharedFiltersModel {
  const isOffline = useIsOffline();
  const projectId = useProjectId();
  const timezone = useProjectTimezone();
  const availableFields = useChartAvailableFieldsSet();
  const runtimes = useMemo(() => charts.map(({ runtime }) => runtime), [charts]);

  const [chosen, setChosen] = useState<ChosenValues>(() => readChosenValues(projectId, definition, timezone));
  const hasChosenRef = useRef(false);

  // Another project (or section): its own chosen values.
  useEffect(() => {
    hasChosenRef.current = false;
    setChosen(readChosenValues(projectId, definition, timezone));
  }, [projectId, definition, timezone]);

  useEffect(() => {
    if (projectId && hasChosenRef.current) {
      LocalStorageManager.setAnalyticsSectionFilters(projectId, definition.section, chosen);
    }
  }, [chosen, projectId, definition]);

  const states = useMemo(
    () =>
      definition.filters
        .map((shared) =>
          resolveSharedFilterState(runtimes, shared, {
            chosen: chosen[shared],
            cleared: clearedSectionFilterValue(shared, timezone),
          })
        )
        .filter((state): state is SharedFilterState => state !== undefined && isVisible(state, availableFields)),
    [runtimes, definition, chosen, timezone, availableFields]
  );
  const sectionValues = useMemo(() => toValues(states), [states]);

  const customChartIds = useMemo(
    () =>
      new Set(
        charts
          .filter(({ runtime }) => hasCustomSharedFilters(runtime, sectionValues))
          .map(({ chartId }) => chartId)
      ),
    [charts, sectionValues]
  );

  const setValue = useCallback(
    (shared: SectionSharedFilter, value: unknown): void => {
      applySectionFilterValue(runtimes, shared, { previous: sectionValues.get(shared), next: value });
      hasChosenRef.current = true;
      setChosen((previous) => ({ ...previous, [shared]: value }));
    },
    [runtimes, sectionValues]
  );

  const activeFilters = useMemo(
    () =>
      new Set(
        states
          .filter(({ shared, value }) => isSectionFilterActive(shared, value, timezone))
          .map(({ shared }) => shared)
      ),
    [states, timezone]
  );

  const clear = useCallback(
    (shared: SectionSharedFilter): void => setValue(shared, clearedSectionFilterValue(shared, timezone)),
    [setValue, timezone]
  );

  const clearAll = useCallback((): void => {
    activeFilters.forEach(clear);
  }, [activeFilters, clear]);

  const restoreSectionFilters = useCallback(
    (chartId: ChartId): void => {
      const chart = charts.find((sectionChart) => sectionChart.chartId === chartId);
      if (chart) {
        restoreSectionValues(chart.runtime, sectionValues);
      }
    },
    [charts, sectionValues]
  );

  const disabled = isOffline && runtimes.some((runtime) => runtime.filtersDisabledOffline);

  return useMemo(
    () => ({
      states,
      setValue,
      clear,
      clearAll,
      activeFilters,
      isMixed: customChartIds.size > 0,
      customChartIds,
      restoreSectionFilters,
      disabled,
    }),
    [states, setValue, clear, clearAll, activeFilters, customChartIds, restoreSectionFilters, disabled]
  );
}
